import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft } from "lucide-react";
import { Masthead } from "@/components/Masthead";
import { Footer } from "@/components/Footer";
import { AudioOverview } from "@/components/AudioOverview";
import { StoryReport } from "@/components/StoryReport";
import { renderArticleBody } from "@/components/articleBody";
import { CATEGORY_COLORS } from "@/components/StoryCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Permanent URL for a single story (/story/:id). This is what email digest
// links point at, and it keeps articles reachable after they rotate off the
// homepage — previously stories only existed inside the homepage modal, so
// once the list refreshed they were unreachable forever.
export const StoryPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | missing
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    setImageError(false);
    (async () => {
      try {
        const res = await axios.get(`${API}/articles/${id}`);
        if (!cancelled) {
          setArticle(res.data);
          setStatus("ok");
        }
      } catch {
        if (!cancelled) setStatus("missing");
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const color = CATEGORY_COLORS[article?.category] || article?.color || "#3A86D4";

  return (
    <div className="scoop-page min-h-screen bg-scoop-cream" data-testid="story-page">
      <Masthead
        activeCategory=""
        onCategoryChange={(cat) => navigate(cat && cat !== "today" ? `/?category=${cat}` : "/")}
      />

      <div className="max-w-[680px] mx-auto px-5 md:px-8 py-8 md:py-12">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-scoop-blue hover:underline mb-6"
          data-testid="story-back-link"
        >
          <ArrowLeft className="w-4 h-4" /> All of today's stories
        </Link>

        {status === "loading" && (
          <div className="bg-white rounded-lg border border-scoop-border p-10 text-center text-scoop-muted">
            Loading the story…
          </div>
        )}

        {status === "missing" && (
          <div className="bg-white rounded-lg border border-scoop-border p-10 text-center">
            <span className="text-6xl block mb-4">🗞️</span>
            <h1 className="font-display text-2xl font-bold text-scoop-ink mb-2">
              This story has moved on
            </h1>
            <p className="text-scoop-muted mb-6">
              We couldn't find that story — it may have been removed. There's
              plenty more to read on today's front page!
            </p>
            <Link
              to="/"
              className="inline-block bg-scoop-blue text-white text-sm font-bold px-6 py-3 rounded-full hover:bg-scoop-blue/90"
            >
              See today's stories →
            </Link>
          </div>
        )}

        {status === "ok" && article && (
          <article className="bg-white rounded-lg border border-scoop-border overflow-hidden">
            {article.image_url && !imageError ? (
              <div className="aspect-[16/9]">
                <img
                  src={article.image_url}
                  alt={article.headline || article.title}
                  className="w-full h-full object-cover"
                  onError={() => setImageError(true)}
                  fetchpriority="high"
                />
              </div>
            ) : (
              <div
                className="aspect-[16/9] flex items-center justify-center"
                style={{ background: `linear-gradient(135deg, #EEF5FF 0%, ${color}18 100%)` }}
              >
                <span className="text-6xl opacity-70">{article.emoji || "📰"}</span>
              </div>
            )}

            <div className="p-7 md:p-10">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                <span
                  className="text-[0.67rem] font-bold tracking-widest uppercase"
                  style={{ color }}
                >
                  {article.sub_category || article.category}
                </span>
              </div>

              <h1
                className="font-display font-black text-[1.7rem] md:text-[2rem] leading-[1.15] tracking-tight text-scoop-ink mb-4"
                data-testid="story-headline"
              >
                {article.headline || article.title}
              </h1>

              <p className="text-[0.95rem] text-scoop-muted leading-relaxed italic mb-5 pb-5 border-b border-scoop-border">
                {article.deck || article.description}
              </p>

              {/* Full narration — the destination end of the preview funnel */}
              <div className="mb-6">
                <AudioOverview article={article} mode="full" />
              </div>

              <div className="font-body-serif text-[1.02rem] text-gray-700 leading-[1.72] tracking-[-0.005em]">
                {renderArticleBody(article.body)}
              </div>

              <StoryReport article={article} />
            </div>
          </article>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default StoryPage;
